import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, ArrowLeft } from "lucide-react";

interface Scenario {
  question: string;
  options: { text: string; score: number; feedback: string }[];
}

const SCENARIOS: Scenario[] = [
  {
    question: "أنت ولأصدقاءك عندكم لعبة وحدة بس... ماذا تفعل؟",
    options: [
      { text: "نلعب بالدور ونتبادل 🔄", score: 10, feedback: "رائع! المشاركة والتبادل تجعل الجميع سعداء!" },
      { text: "ألعب بها وحدي أنا الأول 😤", score: 2, feedback: "لو لعبنا بالدور الكل يستمتع أكثر!" },
      { text: "أبحث عن لعبة ثانية للآخرين 🔍", score: 7, feedback: "تفكير ذكي! لكن المشاركة أيضاً ممتعة!" },
    ],
  },
  {
    question: "صديقك عمل شيء جميل في الفصل... ماذا تقول له؟",
    options: [
      { text: "ماشاء الله! أنت شاطر جداً! 👏", score: 10, feedback: "كلماتك الجميلة تسعد الآخرين وتشجعهم!" },
      { text: "لا أقول شيء 😶", score: 2, feedback: "التشجيع يصنع فرقاً كبيراً! جرّب المرة القادمة!" },
      { text: "أنا أقدر أعمل مثله 💪", score: 4, feedback: "ثقتك بنفسك جميلة، لكن لا تنسَ تشجيع أصدقائك!" },
    ],
  },
  {
    question: "زميلك وقع وتأذى في الملعب... ماذا تفعل؟",
    options: [
      { text: "أساعده يقوم وأنادي المعلمة 🏥", score: 10, feedback: "أنت بطل حقيقي! مساعدة المحتاج شيء عظيم!" },
      { text: "أكمل لعبي ولا أنتبه 🎮", score: 1, feedback: "أصدقاؤنا يحتاجون مساعدتنا عند الحاجة!" },
      { text: "أنظر من بعيد 👀", score: 3, feedback: "لو اقتربت وساعدته سيكون أفضل بكثير!" },
    ],
  },
  {
    question: "عبّر عن شعورك الآن... كيف تحس؟",
    options: [
      { text: "سعيد ومبسوط! 😊", score: 10, feedback: "الحمد لله! شارك سعادتك مع من حولك!" },
      { text: "عادي ومرتاح 😌", score: 8, feedback: "الراحة شعور جميل! استمتع بيومك!" },
      { text: "حزين شوي 😢", score: 8, feedback: "لا بأس أن نحزن أحياناً. تحدث مع أحد تحبه وستشعر بتحسن!" },
    ],
  },
  {
    question: "المعلمة طلبت متطوع لتوزيع الأدوات... ماذا تفعل؟",
    options: [
      { text: "أرفع يدي وأتطوع! ✋", score: 10, feedback: "المبادرة والمساعدة من أجمل الصفات!" },
      { text: "أنتظر أحد غيري يتطوع 🙄", score: 3, feedback: "جرّب المبادرة! ستشعر بسعادة كبيرة!" },
      { text: "أساعد بس لو طلبت مني بالاسم 🤷", score: 5, feedback: "جيد أنك تساعد، لكن المبادرة أجمل!" },
    ],
  },
];

interface Props {
  onComplete: (score: number, stars: number, activityKey: string) => void;
  onBack: () => void;
}

export default function SocialActivity({ onComplete, onBack }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [totalScore, setTotalScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [finished, setFinished] = useState(false);

  const scenario = SCENARIOS[currentIndex];

  const handleSelect = (idx: number) => {
    if (showFeedback) return;
    setSelectedOption(idx);
    setShowFeedback(true);
    setTotalScore(prev => prev + scenario.options[idx].score);
  };

  const handleNext = () => {
    if (currentIndex < SCENARIOS.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    } else {
      setFinished(true);
      const maxScore = SCENARIOS.length * 10;
      const pct = (totalScore / maxScore) * 100;
      const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : 1;
      onComplete(totalScore, stars, "social_scenarios");
    }
  };

  if (finished) {
    const maxScore = SCENARIOS.length * 10;
    const pct = (totalScore / maxScore) * 100;
    const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : 1;
    return (
      <Card className="text-center border-2 border-teal-300 bg-gradient-to-b from-teal-50 to-white">
        <CardContent className="p-8 space-y-4">
          <div className="text-6xl">🫂</div>
          <h2 className="text-2xl font-bold text-foreground">رائع يا بطل الاجتماعي!</h2>
          <div className="flex justify-center gap-1">
            {[1, 2, 3].map(s => (
              <Star key={s} className={`h-10 w-10 ${s <= stars ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
            ))}
          </div>
          <p className="text-lg text-muted-foreground">حصلت على {totalScore} من {maxScore} نقطة</p>
          <Button onClick={onBack} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 ml-2" /> العودة للأنشطة
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button onClick={onBack} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Badge variant="secondary">{currentIndex + 1} / {SCENARIOS.length}</Badge>
      </div>

      <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
        <CardHeader>
          <CardTitle className="text-xl text-center leading-relaxed">{scenario.question}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {scenario.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              disabled={showFeedback}
              className={`w-full p-4 rounded-xl text-right text-lg transition-all border-2 ${
                selectedOption === idx
                  ? opt.score >= 8
                    ? "border-green-400 bg-green-50"
                    : "border-orange-400 bg-orange-50"
                  : "border-muted hover:border-primary/40 bg-card hover:bg-accent"
              } ${showFeedback && selectedOption !== idx ? "opacity-50" : ""}`}
            >
              <div className="flex items-center gap-3">
                {showFeedback && selectedOption === idx && (
                  <CheckCircle className={`h-5 w-5 shrink-0 ${opt.score >= 8 ? "text-green-500" : "text-orange-500"}`} />
                )}
                <span>{opt.text}</span>
              </div>
            </button>
          ))}

          {showFeedback && selectedOption !== null && (
            <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/20 text-center">
              <p className="text-foreground font-medium">{scenario.options[selectedOption].feedback}</p>
            </div>
          )}

          {showFeedback && (
            <Button onClick={handleNext} className="w-full mt-2" size="lg">
              {currentIndex < SCENARIOS.length - 1 ? "السؤال التالي ➡️" : "عرض النتيجة 🫂"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
